import { useRef, useEffect, useState } from 'react';
import { EditorView, keymap, lineNumbers, highlightActiveLine, dropCursor, rectangularSelection } from '@codemirror/view';
import { EditorState, StateEffect } from '@codemirror/state';
import { defaultHighlightStyle, syntaxHighlighting, indentOnInput, bracketMatching, foldKeymap } from '@codemirror/language';
import { markdown } from '@codemirror/lang-markdown';
import { oneDark } from '@codemirror/theme-one-dark';
import { defaultKeymap, history, historyKeymap, indentWithTab } from '@codemirror/commands';
import { useAppContext } from '../../context/AppContext';
import { useTheme } from '../../context/ThemeProvider';

const EditorPanel = () => {
  const editorRef = useRef<HTMLDivElement>(null);
  const viewRef = useRef<EditorView | null>(null);
  const { state, dispatch } = useAppContext();
  const { isDark } = useTheme();
  const [wordCount, setWordCount] = useState(0);
  const [characterCount, setCharacterCount] = useState(0);
  const [cursorPosition, setCursorPosition] = useState({ line: 1, col: 1 });

  // Custom theme for Content Studio
  const contentStudioTheme = EditorView.theme({
    '&': {
      height: '100%',
      fontSize: `${state.preferences.editorFontSize}px`,
      fontFamily: 'var(--cs-font-family-mono)',
      backgroundColor: isDark ? 'var(--cs-gray-800)' : 'white',
    },
    '.cm-content': {
      padding: '24px',
      minHeight: '100%',
      lineHeight: 'var(--cs-line-height-relaxed)',
      color: isDark ? 'var(--cs-gray-100)' : 'var(--cs-gray-900)',
      caretColor: 'var(--cs-primary-500)',
    },
    '.cm-heading-1': { fontSize: '1.8em', fontWeight: 'bold' },
    '.cm-heading-2': { fontSize: '1.5em', fontWeight: 'bold' },
    '.cm-heading-3': { fontSize: '1.3em', fontWeight: 'bold' },
    '.cm-heading-4': { fontSize: '1.1em', fontWeight: 'bold' },
    '.cm-heading-5': { fontSize: '1.0em', fontWeight: 'bold' },
    '.cm-heading-6': { fontSize: '0.9em', fontWeight: 'bold' },
    '.cm-header-mark': {
      color: isDark ? 'var(--cs-gray-600)' : 'var(--cs-gray-400)',
      fontWeight: 'bold',
    },
    '.cm-focused': {
      outline: 'none',
    },
    '.cm-editor': {
      height: '100%',
    },
    '.cm-scroller': {
      height: '100%',
      fontFamily: 'var(--cs-font-family-mono)',
    },
    '.cm-line': {
      padding: '2px 24px',
    },
    '.cm-activeLine': {
      backgroundColor: isDark ? 'rgba(255, 255, 255, 0.04)' : 'rgba(0, 0, 0, 0.04)',
      borderRadius: '4px',
    },
    '.cm-activeLineGutter': {
      backgroundColor: 'transparent',
    },
    '.cm-lineNumbers': {
      color: isDark ? 'var(--cs-gray-500)' : 'var(--cs-gray-400)',
      fontFamily: 'var(--cs-font-family-mono)',
      fontSize: '13px',
      paddingRight: '16px',
    },
    '.cm-gutters': {
      backgroundColor: isDark ? 'var(--cs-gray-800)' : 'white',
      borderColor: isDark ? 'var(--cs-gray-700)' : 'var(--cs-gray-200)',
      borderRight: '1px solid',
      paddingLeft: '24px',
    },
    '.cm-selectionBackground': {
      backgroundColor: 'var(--cs-primary-200) !important',
    },
    '.dark .cm-selectionBackground': {
      backgroundColor: 'var(--cs-primary-700) !important',
    },
    '.cm-searchMatch': {
      backgroundColor: 'var(--cs-warning-200)',
      border: '1px solid var(--cs-warning-400)',
      borderRadius: '2px',
    },
    '.dark .cm-searchMatch': {
      backgroundColor: 'var(--cs-warning-700)',
      border: '1px solid var(--cs-warning-500)',
    },
    '&.cm-editor.cm-focused .cm-cursor': {
      borderColor: 'var(--cs-primary-500)',
      borderWidth: '2px',
    },
  }, { dark: isDark });

  // Create editor extensions
  const createExtensions = () => [
    lineNumbers(),
    highlightActiveLine(),
    dropCursor(),
    indentOnInput(),
    bracketMatching(),
    rectangularSelection(),
    syntaxHighlighting(defaultHighlightStyle, { fallback: true }),
    markdown(),
    history(),
    isDark ? oneDark : [],
    contentStudioTheme,
    keymap.of([
      ...defaultKeymap,
      ...historyKeymap,
      ...foldKeymap,
      indentWithTab,
      {
        key: 'Mod-k',
        run: () => {
          dispatch({ type: 'TOGGLE_EXPORT_MODAL' });
          return true;
        },
      },
    ]),
    EditorView.updateListener.of((update) => {
      if (update.docChanged) {
        const newContent = update.state.doc.toString();
        dispatch({ type: 'SET_CONTENT', payload: newContent });
        
        const words = newContent.trim() ? newContent.trim().split(/\s+/).length : 0;
        setWordCount(words);
        setCharacterCount(newContent.length);
      }
      
      if (update.selectionSet) {
        const pos = update.state.selection.main.head;
        const line = update.state.doc.lineAt(pos);
        setCursorPosition({
          line: line.number,
          col: pos - line.from + 1
        });
      }
    }),
  ];

  // Initialize CodeMirror editor
  useEffect(() => {
    if (editorRef.current && !viewRef.current) {
      const startState = EditorState.create({
        doc: state.document.content,
        extensions: createExtensions(),
      });

      viewRef.current = new EditorView({
        state: startState,
        parent: editorRef.current,
      });
      
      // Initial counts
      const content = state.document.content;
      const words = content.trim() ? content.trim().split(/\s+/).length : 0;
      setWordCount(words);
      setCharacterCount(content.length);
    }

    return () => {
      if (viewRef.current) {
        viewRef.current.destroy();
        viewRef.current = null;
      }
    };
  }, []);

  // Update editor when theme or settings change
  useEffect(() => {
    if (viewRef.current) {
      viewRef.current.dispatch({
        effects: StateEffect.reconfigure.of(createExtensions()),
      });
    }
  }, [isDark, state.preferences.editorFontSize]);

  // Update editor content when document changes (from external sources)
  useEffect(() => {
    if (viewRef.current) {
      const currentContent = viewRef.current.state.doc.toString();
      if (currentContent !== state.document.content) {
        viewRef.current.dispatch({
          changes: {
            from: 0,
            to: currentContent.length,
            insert: state.document.content,
          },
        });
      }
    }
  }, [state.document.content]);

  return (
    <div className="h-full flex flex-col cs-panel">
      {/* Editor Toolbar */}
      <div className="cs-panel-header flex items-center justify-between text-sm">
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2">
            <svg className="w-4 h-4 text-gray-500 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
            <span className="font-medium text-gray-700 dark:text-gray-300">Editor</span>
          </div>
          
          <div className="hidden md:flex items-center space-x-3 text-xs text-gray-500 dark:text-gray-400">
            <span>Ln {cursorPosition.line}, Col {cursorPosition.col}</span>
            <span>•</span>
            <span>{wordCount} words</span>
            <span>•</span>
            <span>{characterCount} characters</span>
          </div>
        </div>
        
        <div className="flex items-center space-x-2">
          <div className="text-xs text-gray-500 dark:text-gray-400">
            <span className="hidden sm:inline">Markdown</span>
          </div>
        </div>
      </div>

      {/* Editor Container */}
      <div className="flex-1 overflow-hidden relative">
        <div 
          ref={editorRef} 
          className="h-full w-full cs-animate-fade-in"
        />
        
        {/* Focus indicator */}
        <div className="absolute inset-x-0 top-0 h-0.5 bg-gradient-to-r from-transparent via-blue-500/30 to-transparent opacity-0 focus-within:opacity-100 transition-opacity duration-300"></div>
      </div>

      {/* Mobile Status Bar */}
      <div className="md:hidden px-4 py-2 bg-gray-50 dark:bg-gray-750 border-t border-gray-200 dark:border-gray-700 text-xs text-gray-500 dark:text-gray-400">
        <div className="flex items-center justify-between">
          <span>Ln {cursorPosition.line}, Col {cursorPosition.col}</span>
          <div className="flex items-center space-x-3">
            <span>{wordCount} words</span>
            <span>•</span>
            <span>{characterCount} chars</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditorPanel;